<script setup>
// NOOVI (noovi-ai-autonomous — Fase 49-B): resumo de conversa gerado pela IA
// nativa (Noovi::Ai). Lê o resumo persistido ao montar (GET) e gera/regenera
// sob demanda (POST). Estados: carregando / vazio / sucesso / erro.
import { ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import ConversationApi from 'dashboard/api/conversations';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const summary = ref('');
const generatedAt = ref(null);
const isLoading = ref(false);
const isGenerating = ref(false);

const fetchSummary = async () => {
  if (!props.conversationId) return;
  isLoading.value = true;
  try {
    const { data } = await ConversationApi.getSummary(props.conversationId);
    summary.value = data.summary || '';
    generatedAt.value = data.summary_generated_at || null;
  } catch (error) {
    summary.value = '';
    generatedAt.value = null;
  } finally {
    isLoading.value = false;
  }
};

const generateSummary = async () => {
  if (!props.conversationId || isGenerating.value) return;
  isGenerating.value = true;
  try {
    const { data } = await ConversationApi.generateSummary(
      props.conversationId
    );
    summary.value = data.summary || '';
    generatedAt.value = data.summary_generated_at || null;
  } catch (error) {
    const status = error?.response?.status;
    const message =
      status === 503
        ? t('CONVERSATION_SUMMARY.UNAVAILABLE')
        : t('CONVERSATION_SUMMARY.ERROR');
    useAlert(message);
  } finally {
    isGenerating.value = false;
  }
};

watch(
  () => props.conversationId,
  () => fetchSummary(),
  { immediate: true }
);
</script>

<template>
  <div class="py-2">
    <div v-if="isLoading" class="flex items-center justify-center py-4">
      <span class="i-lucide-loader-2 animate-spin text-n-slate-10" />
    </div>

    <div v-else class="flex flex-col gap-2">
      <p
        v-if="summary"
        class="m-0 text-sm leading-relaxed whitespace-pre-line text-n-slate-12"
      >
        {{ summary }}
      </p>
      <p v-else class="m-0 text-xs text-n-slate-10">
        {{ $t('CONVERSATION_SUMMARY.EMPTY') }}
      </p>

      <span v-if="summary && generatedAt" class="text-xs text-n-slate-10">
        {{
          $t('CONVERSATION_SUMMARY.UPDATED_AT', {
            time: dynamicTime(generatedAt),
          })
        }}
      </span>

      <button
        class="inline-flex items-center justify-center gap-2 h-8 px-3 mt-1 text-sm rounded-lg border-0 outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10 transition-all duration-200 disabled:opacity-50"
        :disabled="isGenerating"
        @click="generateSummary"
      >
        <span
          v-if="isGenerating"
          class="i-lucide-loader-2 animate-spin flex-shrink-0"
        />
        <span v-else class="i-lucide-sparkles flex-shrink-0" />
        {{
          isGenerating
            ? $t('CONVERSATION_SUMMARY.GENERATING')
            : summary
              ? $t('CONVERSATION_SUMMARY.REGENERATE')
              : $t('CONVERSATION_SUMMARY.GENERATE')
        }}
      </button>
    </div>
  </div>
</template>
